import React, { useState, useEffect, forwardRef, useRef } from 'react';
import { DocumentData } from  '../../context/DocumentDataProvider';
import event_buffer from "../../context/event_def";
import { Node } from '@xyflow/react';
import {CustomNodeData} from '../tabs/ReactFlowTab';
import { LoadedModel } from './ThreeDTab';

// type CustomNodeData = {
//     label: string;
//     inputs: number;
//     outputs: number;
//     custom_data?: { [key: string]: any };
// };

// type CustomNode = {
//     data: CustomNodeData;
//     // add other properties as needed
// };

interface PropertyGridTabProps {
    nodes: Node<CustomNodeData>[];
    setNodes: React.Dispatch<React.SetStateAction<Node<CustomNodeData>[]>>;
    selectedNodeIndex: number | null;
    setSelectedNodeIndex: React.Dispatch<React.SetStateAction<number | null>>;
    loadedModels: LoadedModel[];
    setLoadedModels: React.Dispatch<React.SetStateAction<LoadedModel[]>>;
}

enum DataGridType_enum {
    node,
    threeD,
};


// Display modes for number formatting
enum DisplayMode {
    Engineering = 'engineering',
    Scientific = 'scientific',
    Plain = 'plain',
}

// Helper: multipliers for SI prefixes
const SI_MULTIPLIERS: { [k: string]: number } = {
    T: 1e12,
    G: 1e9,
    M: 1e6,
    k: 1e3,
    K: 1e3,
    m: 1e-3,
    u: 1e-6,
    μ: 1e-6,
    'µ': 1e-6,
    n: 1e-9,
    p: 1e-12,
};

// Parse strings with SI style units. Accept formats like:
//  - '150m' => 0.15
//  - '16u'  => 1.6e-5
//  - '1k2'  => treat as '1.2k' => 1200
// Also accept normal decimal and scientific notation.
function parseEngineering(input: string): number | null {
    if (!input) return null;
    const s = String(input).trim();
    if (s === '') return null;

    // direct numeric or scientific
    if (/^[+-]?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?$/.test(s)) {
        const n = Number(s);
        return isNaN(n) ? null : n;
    }

    // find first SI prefix letter in string
    const prefixChars = Object.keys(SI_MULTIPLIERS).join('');
    // look for any of the prefix chars; match position
    const regex = new RegExp(`([+-]?[0-9]*)([${prefixChars}])([0-9]*)$`);
    const m = s.match(regex);
    if (m) {
        // groups: (before)(prefix)(after)
        const before = m[1] || '0';
        const prefix = m[2];
        const after = m[3] || '';
        const multiplier = SI_MULTIPLIERS[prefix];
        if (multiplier === undefined) return null;
        // combine before and after as a decimal: before.after
        const combined = after ? `${before}.${after}` : before;
        const num = Number(combined);
        if (isNaN(num)) return null;
        return num * multiplier;
    }

    // also accept trailing prefix after a float: e.g. '1.5k'
    const regex2 = new RegExp(`^([+-]?[0-9]*\.?[0-9]+)([${prefixChars}])$`);
    const m2 = s.match(regex2);
    if (m2) {
        const num = Number(m2[1]);
        const prefix = m2[2];
        const multiplier = SI_MULTIPLIERS[prefix];
        if (isNaN(num) || multiplier === undefined) return null;
        return num * multiplier;
    }

    return null;
}

// Format a number according to display mode. Engineering uses SI prefixes with
// exponent multiples of 3.
function formatNumber(n: number, mode: DisplayMode): string {
    if (!isFinite(n)) return String(n);
    if (mode === DisplayMode.Plain) return String(n);
    if (mode === DisplayMode.Scientific) {
        // 3 significant digits
        return Number(n).toExponential(3).replace(/e\+?/, 'e');
    }

    // engineering
    const abs = Math.abs(n);
    if (abs === 0) return '0';
    const exp = Math.floor(Math.log10(abs) / 3) * 3;
    const mant = n / Math.pow(10, exp);
    // mapping exponent to prefix
    const expToPrefix: { [k: string]: string } = {
        '12': 'T',
        '9': 'G',
        '6': 'M',
        '3': 'k',
        '0': '',
        '-3': 'm',
        '-6': 'u',
        '-9': 'n',
        '-12': 'p',
    };
    const prefix = expToPrefix[String(exp)] ?? `e${exp}`;
    // choose precision: show up to 3 significant digits but trim
    const absMant = Math.abs(mant);
    let digits = 3;
    if (absMant >= 100) digits = 0;
    else if (absMant >= 10) digits = 1;
    else digits = 2;
    // use toFixed for mantissa digits after decimal
    const fixed = mant.toFixed(Math.max(0, digits - Math.floor(Math.log10(Math.abs(mant))) - 1));
    return `${fixed}${prefix}`;
}

const SEPARATOR_COLOR = 'rgba(0,0,0,0.2)';
const SEPARATOR_WIDTH = 2; // px
const SEPARATOR_HEIGHT = 45; // px
const SEPARATOR_GAP = 5; // px horizontal gap around separator

const separatorStyle_h: React.CSSProperties = {
    width:  '95%',
    height: SEPARATOR_WIDTH,
    background: SEPARATOR_COLOR, //'#8d393950',
    margin: `0 ${SEPARATOR_GAP}px`,
    borderRadius: 1,
};

// Algoritmo recursivo para renderizar y editar cualquier objeto
const RecursivePropertyGrid: React.FC<{ data: any; onChange: (d: any) => void; path?: string; onCommit?: (d?: any) => void; displayMode: DisplayMode; exposeField?: (name: string, type: string) => void; unexposeField?: (name: string) => void; setPropertyDescription?: React.Dispatch<React.SetStateAction<string>> }> = ({ data, onChange, path = '', onCommit, displayMode, exposeField, unexposeField, setPropertyDescription }) => {
    // keep a ref to the latest data so we can commit the exact value on blur/enter
    const latestDataRef = useRef<any>(data);
    useEffect(() => {
        latestDataRef.current = data;
    }, [data]);

    // Handler para cambios (committed values)
    const handleChange = (key: string, value: any) => {
        const original = data[key];
        let castedValue = value;
        if (typeof original === 'number') {
            castedValue = typeof value === 'number' ? value : (value === '' ? 0 : Number(value));
        } else if (typeof original === 'boolean') {
            castedValue = value === 'true' || value === true;
        } else if (typeof original === 'object' && original !== null) {
            castedValue = value;
        }
        const updated = { ...data, [key]: castedValue };
        latestDataRef.current = updated;
        onChange(updated);
    };

    // local edit buffer for in-progress edits (keyed by path+key)
    const [editBuffer, setEditBuffer] = React.useState<Record<string, string>>({});
    useEffect(() => {
        // clear edit buffer when data changes externally
        setEditBuffer({});
    }, [data, displayMode]);

    // Render recursivo con estilo profesional
        // Si el objeto es un nodo 'leaf' que tiene la propiedad _Value, renderizamos un editor directo

        if (data && typeof data === 'object' && Object.prototype.hasOwnProperty.call(data, '_Value')) {
            const val = data._Value;
            const keyPath = path + '_Value';
            const buffer = editBuffer[keyPath];
            const display = buffer !== undefined ? buffer : (typeof val === 'number' ? formatNumber(val, displayMode as DisplayMode) : String(val));
            const variableType : string = data._Type;
            const description : string = data._Description || '';

            console.log("variableType (leaf):", variableType);

            const commitValue = (newVal: any) => {
                const updated = { ...data, _Value: newVal };
                latestDataRef.current = updated;
                onChange(updated);
                if (onCommit) onCommit(latestDataRef.current);
            };

            return (
                <div
                    style={{
                        marginLeft: path ? 16 : 0,
                        borderLeft: path ? '2px solid #e0e0e0' : undefined,
                        paddingLeft: path ? 16 : 0,
                        background: path ? '#fafbfc' : 'transparent',
                        borderRadius: path ? 8 : 0,
                        boxShadow: path ? '0 1px 4px rgba(0,0,0,0.04)' : undefined,
                        marginBottom: 8,
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
                        <label style={{ minWidth: 90, fontWeight: 500, color: '#333', fontSize: 14, flex: '0 0 auto', marginRight: 8 }}>_Value:</label>
                        {typeof val === 'number' ? (
                            <input
                                type="text"
                                value={display}
                                onChange={e => {
                                    const v = e.target.value;
                                    setEditBuffer(prev => ({ ...prev, [keyPath]: v }));
                                }}
                                onFocus={e => {
                                    setEditBuffer(prev => ({ ...prev, [keyPath]: String(val) }));
                                    setPropertyDescription && setPropertyDescription(description);
                                }}
                                onBlur={e => {
                                    const raw = editBuffer[keyPath] ?? e.target.value;
                                    const parsed = parseEngineering(raw);
                                    if (parsed !== null) {
                                        commitValue(parsed);
                                    }
                                    setEditBuffer(prev => {
                                        const c = { ...prev };
                                        delete c[keyPath];
                                        return c;
                                    });
                                }}
                                onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLElement).blur(); }}
                                style={{
                                    flex: '1 1 80px',
                                    minWidth: 60,
                                    maxWidth: 120,
                                    padding: '4px 8px',
                                    borderRadius: 4,
                                    border: '1px solid #bdbdbd',
                                    fontSize: 14,
                                }}
                            />
                        ): variableType === 'Simulador.SimuMath.LinealDataTable' ? (
                            <div> 
                                <label>Lineal Table </label>
                            </div>
                        
                        ) : typeof val === 'boolean' ? (
                            <select
                                value={val ? 'true' : 'false'}
                                onChange={e => {
                                    const newV = e.target.value === 'true';
                                    commitValue(newV);
                                }}
                                onBlur={() => onCommit && onCommit(latestDataRef.current)}
                                onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLElement).blur(); }}
                                style={{
                                    flex: '1 1 80px',
                                    minWidth: 60,
                                    maxWidth: 120,
                                    padding: '4px 8px',
                                    borderRadius: 4,
                                    border: '1px solid #bdbdbd',
                                    fontSize: 14,
                                }}
                            >
                                <option value="true">True</option>
                                <option value="false">False</option>
                            </select>
                        ) : (
                            <input
                                type="text"
                                value={typeof val === 'string' || typeof val === 'number' ? String(val) : ''}
                                onChange={e => {
                                    const newV = e.target.value;
                                    const updated = { ...data, _Value: newV };
                                    latestDataRef.current = updated;
                                    onChange(updated);
                                }}
                                onFocus={e => {
                                    setPropertyDescription && setPropertyDescription(description);
                                }}
                                onBlur={() => onCommit && onCommit(latestDataRef.current)}
                                onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLElement).blur(); }}
                                style={{
                                    flex: '1 1 180px',
                                    minWidth: 80,
                                    maxWidth: 320,
                                    padding: '4px 8px',
                                    borderRadius: 4,
                                    border: '1px solid #bdbdbd',
                                    fontSize: 14,
                                }}
                            />
                        )}
                    </div>
                </div>
            );
        }

        return (
            <div
                style={{
                    marginLeft: path ? 16 : 0,
                    borderLeft: path ? '2px solid #e0e0e0' : undefined,
                    paddingLeft: path ? 16 : 0,
                    background: path ? '#fafbfc' : 'transparent',
                    borderRadius: path ? 8 : 0,
                    boxShadow: path ? '0 1px 4px rgba(0,0,0,0.04)' : undefined,
                    marginBottom: 8,
                }}
            >
                {Object.entries(data).map(([key, value]) => (
                    // Si el valor es un objeto y contiene _Value, renderizamos inline usando el nombre del campo (key)
                    typeof value === 'object' && value !== null && Object.prototype.hasOwnProperty.call(value, '_Value') ? (
                        (() => {
                            const val = (value as any)._Value;
                            const variableType : string = (value as any)._Type;
                            const description: string = (value as any)._Description || '';
                            const keyPath = path + key;
                            const buffer = editBuffer[keyPath];
                            const display = buffer !== undefined ? buffer : (typeof val === 'number' ? formatNumber(val, displayMode as DisplayMode) : String(val));
                            const commitLeaf = (newVal: any) => {
                                const updatedObj = { ...value, _Value: newVal };
                                handleChange(key, updatedObj);
                                if (onCommit) onCommit(latestDataRef.current);
                            };

                            return (
                                <div key={path + key} style={{ display: 'flex', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
                                    <label style={{ minWidth: 90, fontWeight: 500, color: '#333', fontSize: 14, flex: '0 0 auto', marginRight: 8 }}>{key}:</label>
                                    {typeof val === 'number' ? (
                                        <div> 
                                            <input
                                                type="text"
                                                value={display}
                                                onChange={e => setEditBuffer(prev => ({ ...prev, [keyPath]: e.target.value }))}
                                                onFocus={e => {
                                                    setEditBuffer(prev => ({ ...prev, [keyPath]: String(val) }));
                                                    setPropertyDescription && setPropertyDescription(description);
                                                }}
                                                onBlur={e => {
                                                    const raw = editBuffer[keyPath] ?? (e.target as HTMLInputElement).value;
                                                    const parsed = parseEngineering(raw);
                                                    if (parsed !== null) commitLeaf(parsed);
                                                    setEditBuffer(prev => { const c = { ...prev }; delete c[keyPath]; return c; });
                                                }}
                                                onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLElement).blur(); }}
                                                style={{ flex: '1 1 80px', minWidth: 60, maxWidth: 120, padding: '4px 8px', borderRadius: 4, border: '1px solid #bdbdbd', fontSize: 14 }}
                                            />
                                            <button onClick={() => exposeField && exposeField(path + key, variableType)} > <img src="/share.svg" alt="Share" width={18} height={18} /> </button>
                                            <button onClick={() => unexposeField && unexposeField(path +key)} > <img src="/not_share.svg" alt="Unshare" width={18} height={18} /> </button>
                                        </div>
                                    ): typeof val === 'boolean' ? (
                                        <select
                                            value={val ? 'true' : 'false'}
                                            onChange={e => {commitLeaf(e.target.value === 'true');
                                                // const description: string = (value as any)._Description || '';
                                                setPropertyDescription && setPropertyDescription( description);
                                            }}
                                            onBlur={() => onCommit && onCommit(latestDataRef.current)}
                                            onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLElement).blur(); }}
                                            style={{ flex: '1 1 80px', minWidth: 60, maxWidth: 120, padding: '4px 8px', borderRadius: 4, border: '1px solid #bdbdbd', fontSize: 14 }}
                                            onFocus={e => {
                                                // const description: string = (value as any)._Description || '';
                                                setPropertyDescription && setPropertyDescription(description);
                                            }}
                                        >
                                            <option value="true">True</option>
                                            <option value="false">False</option>
                                        </select>
                                    ) : (
                                        <input
                                            type="text"
                                            value={typeof val === 'string' || typeof val === 'number' ? String(val) : ''}
                                            onChange={e => {
                                                const newV = e.target.value;
                                                const updatedObj = { ...value, _Value: newV };
                                                handleChange(key, updatedObj);
                                                
                                            }}
                                            onBlur={() => onCommit && onCommit(latestDataRef.current)}
                                            onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLElement).blur(); }}
                                            style={{ flex: '1 1 180px', minWidth: 80, maxWidth: 320, padding: '4px 8px', borderRadius: 4, border: '1px solid #bdbdbd', fontSize: 14 }}
                                            onFocus={e => {
                                                const description: string = (value as any)._Description || '';
                                                // const variableTypeLocal: string = (value as any)._Type || '';
                                                setPropertyDescription && setPropertyDescription(description);
                                            }}
                                        />
                                    )}
                                </div>
                            );
                        })()
                    ) : (
                        typeof value === 'object' && value !== null ? (
                            <div key={path + key} style={{ marginBottom: 12 }}>
                                <div style={{ fontWeight: 600, color: '#8d3939ff', marginBottom: 4, fontSize: 15 }}>
                                    {key} {String((value as any)._Type).startsWith("Simulador.SimuMath.Tables.") ? (
                                        <div>
                                            Table <button onClick={() => exposeField && exposeField(path + key, "Simulador.SimuMath.IDataTable")} > <img src="/share.svg" alt="Share" width={18} height={18} /></button>
                                            <button onClick={() => unexposeField && unexposeField(path +key)} > <img src="/not_share.svg" alt="Unshare" width={18} height={18} /> </button>
                                        </div>
                                    ) : ("")}
                                </div>
                                <RecursivePropertyGrid
                                    data={value}
                                    onChange={v => handleChange(key, v)}
                                    path={path + key + '.'}
                                    onCommit={(childData) => {
                                        // merge the child's committed object into this level under `key`
                                        handleChange(key, childData);
                                        // now propagate the full updated object upwards
                                        if (onCommit) onCommit(latestDataRef.current);
                                    }}
                                    displayMode={displayMode}
                                    exposeField={exposeField}
                                    unexposeField={unexposeField}
                                    setPropertyDescription={setPropertyDescription}
                                />
                            </div>
                        ) : (
                            key != "_Type" && key != "_Description" ? (
                                <div key={path + key} style={{ display: 'flex', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
                                    <label style={{ minWidth: 90, fontWeight: 500, color: '#333', fontSize: 14, flex: '0 0 auto', marginRight: 8 }}>{key}:</label>
                                    {typeof value === 'number' ? (
                                            (() => {
                                                const keyPath = path + key;
                                                const buffer = editBuffer[keyPath];
                                                const description: string = editBuffer[keyPath + '_Description'] || '';
                                                const display = buffer !== undefined ? buffer : formatNumber(value, displayMode as DisplayMode);
                                                return (
                                                    <input
                                                        type="text"
                                                        value={display}
                                                        onChange={e => {
                                                            const v = e.target.value;
                                                            setEditBuffer(prev => ({ ...prev, [keyPath]: v }));
                                                        }}
                                                        onFocus={e => {
                                                            // on focus show a raw representation (user can edit)
                                                            const keyPathF = path + key;
                                                            setEditBuffer(prev => ({ ...prev, [keyPathF]: String(value) }));
                                                           
                                                            setPropertyDescription && setPropertyDescription(description);
                                                        }}
                                                        onBlur={e => {
                                                            const keyPathB = path + key;
                                                            const raw = editBuffer[keyPathB] ?? e.target.value;
                                                            // try parse engineering/scientific/decimal
                                                            const parsed = parseEngineering(raw);
                                                            if (parsed !== null) {
                                                                handleChange(key, parsed);
                                                                // commit upward
                                                                if (onCommit) onCommit(latestDataRef.current);
                                                            }
                                                            // clear buffer after commit
                                                            setEditBuffer(prev => {
                                                                const c = { ...prev };
                                                                delete c[keyPathB];
                                                                return c;
                                                            });
                                                        }}
                                                        onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLElement).blur(); }}
                                                        style={{
                                                            flex: '1 1 80px',
                                                            minWidth: 60,
                                                            maxWidth: 120,
                                                            padding: '4px 8px',
                                                            borderRadius: 4,
                                                            border: '1px solid #bdbdbd',
                                                            fontSize: 14,
                                                        }}
                                                    />
                                                );
                                            })()
                                        ) : typeof value === 'boolean' ? (
                                        <select
                                            value={value ? 'true' : 'false'}
                                            onChange={e => handleChange(key, e.target.value)}
                                            onBlur={() => onCommit && onCommit(latestDataRef.current)}
                                            onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLElement).blur(); }}
                                            style={{
                                                flex: '1 1 80px',
                                                minWidth: 60,
                                                maxWidth: 120,
                                                padding: '4px 8px',
                                                borderRadius: 4,
                                                border: '1px solid #bdbdbd',
                                                fontSize: 14,
                                            }}
                                        >
                                            <option value="true">True</option>
                                            <option value="false">False</option>
                                        </select>
                                    ) : (
                                        <input
                                            type="text"
                                            value={typeof value === 'string' || typeof value === 'number' ? String(value) : ''}
                                            onChange={e => handleChange(key, e.target.value)}
                                            onBlur={() => onCommit && onCommit(latestDataRef.current)}
                                            onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLElement).blur(); }}
                                            style={{
                                                flex: '1 1 180px',
                                                minWidth: 80,
                                                maxWidth: 320,
                                                padding: '4px 8px',
                                                borderRadius: 4,
                                                border: '1px solid #bdbdbd',
                                                fontSize: 14,
                                            }}
                                            onFocus={e => {
                                                // on focus show a raw representation (user can edit)
                                                // const keyPathF = path + key;
                                                // setEditBuffer(prev => ({ ...prev, [keyPathF]: String(value) }));
                                                const description: string = (value as any)._Description || '';
                                                setPropertyDescription && setPropertyDescription(description);
                                            }}
                                        />
                                    )}
                                </div>
                            ) : (
                                <div></div>
                            )
                        )
                    )
                ))}
            </div>
        );
};


const PropertyGridTab: React.FC<PropertyGridTabProps> = ({nodes, setNodes, selectedNodeIndex, setSelectedNodeIndex, loadedModels, setLoadedModels}) => {
    const [nodeData, setNodeData] = useState<CustomNodeData>({ label: '', inputs: [], outputs: [], custom_data: {} });
    const [dataGridType, setDataGridType] = useState<DataGridType_enum>(DataGridType_enum.node);
    const [displayMode, setDisplayMode] = useState<DisplayMode>(DisplayMode.Engineering);
    const [propertyDescription, setPropertyDescription] = useState<string>('Select a property to see its description');

    // Actualiza todos los campos del nodo seleccionado
    // If newData is provided, save that immediately; otherwise use current nodeData state
    const handleSaveMeta = (newData?: CustomNodeData) => {
        if (dataGridType == DataGridType_enum.node) {
            setNodes((prev) => {
                if (selectedNodeIndex !== null) {
                    const dataToSave = newData ?? nodeData;
                    const updatedNode = { ...prev[selectedNodeIndex], data: { ...dataToSave } };
                    const newNodes = [...prev];
                    newNodes[selectedNodeIndex] = updatedNode;
                    return newNodes;
                }
                return prev;
            });
        }

        setLoadedModels((prev) => {
                return prev.map((model, index) => {
                    // data.object._Properties.representation_unique_id?._Value
                    if (model.id == newData?.custom_data?.object?._Properties?.representation_unique_id?._Value) {

                        const short_cut = newData?.custom_data?.object._Properties;
                        model.scene.position.set(short_cut.pos_x._Value * 100,
                                            short_cut.pos_y._Value * 100,
                                            short_cut.pos_z._Value * 100);
                        // model.scene.scale.set(short_cut.scale_x._Value,
                        //                      short_cut.scale_y._Value,
                        //                      short_cut.scale_z._Value);
                        model.scene.rotation.set(short_cut.rot_y._Value * Math.PI / 180,
                                                short_cut.rot_x._Value * Math.PI / 180,
                                                short_cut.rot_z._Value * Math.PI / 180);     
                    }
                    return model;
            });
        });   
    };

    useEffect(() => {
        if (selectedNodeIndex !== null) {
            if (nodes.length  > selectedNodeIndex) {
                const elem = nodes[selectedNodeIndex].data as CustomNodeData;
                setNodeData({ ...elem });
                setDataGridType(DataGridType_enum.node);
            };
        }
    }, [selectedNodeIndex, nodes]);
    
    // Expose a field as an input handle on the selected ReactFlow node
    const exposeField = (fieldName: string, fieldType: string) => {
        if (selectedNodeIndex === null) return;
        console.log("Exposing field:", fieldName);
        console.log("fieldname:",  fieldName);
        // Update the ReactFlow nodes array: add input if not present
        setNodes(prev => {
            const newNodes = [...prev];
            if (selectedNodeIndex >= newNodes.length) return prev;
            const node = { ...newNodes[selectedNodeIndex] };
            const data = { ...(node.data as CustomNodeData) };
            const inputs = Array.isArray(data.inputs) ? [...data.inputs] : [];
            const exists = inputs.some(i => i.name === fieldName);
            if (!exists) {
                inputs.push({ name: fieldName, type: fieldType });
                data.inputs = inputs;
                node.data = data;
                newNodes[selectedNodeIndex] = node;
            }
            return newNodes;
        });

        // Keep local nodeData in sync so the property grid reflects the change
        setNodeData(prev => {
            const inputs = Array.isArray(prev.inputs) ? [...prev.inputs] : [];
            if (inputs.some(i => i.name === fieldName)) return prev;
            return { ...prev, inputs: [...inputs, { name: fieldName, type: 'System.Double' }] };
        });
    };

    // Remove an exposed field (input handle) from the selected ReactFlow node
    const unexposeField = (fieldName: string) => {
        if (selectedNodeIndex === null) return;

        setNodes(prev => {
            const newNodes = [...prev];
            if (selectedNodeIndex >= newNodes.length) return prev;
            const node = { ...newNodes[selectedNodeIndex] };
            const data = { ...(node.data as CustomNodeData) };
            const inputs = Array.isArray(data.inputs) ? [...data.inputs] : [];
            const filtered = inputs.filter(i => i.name !== fieldName);
            if (filtered.length !== inputs.length) {
                data.inputs = filtered;
                node.data = data;
                newNodes[selectedNodeIndex] = node;
            }
            return newNodes;
        });

        // Sync local nodeData
        setNodeData(prev => {
            const inputs = Array.isArray(prev.inputs) ? [...prev.inputs] : [];
            const filtered = inputs.filter(i => i.name !== fieldName);
            if (filtered.length === inputs.length) return prev;
            return { ...prev, inputs: filtered };
        });
    };
        
    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div style={{ padding: 16, flex: '0 0 auto' }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                    <label style={{ marginRight: 8, fontWeight: 600 , color: '#8d3939ff'}}>Number format:</label>
                    <select value={displayMode} onChange={e => setDisplayMode(e.target.value as DisplayMode)} style={{ marginRight: 12 }}>
                        <option value={DisplayMode.Engineering}>Engineering (k, M, m, u)</option>
                        <option value={DisplayMode.Scientific}>Scientific</option>
                        <option value={DisplayMode.Plain}>Plain</option>
                    </select>
                </div>
                <div style={separatorStyle_h} />
            </div>
            
            <div style={{ padding: 16, overflowY: 'auto', flex: 1 }}>
            {
                (() => {
                    const propsObj = nodeData.custom_data?.object?._Properties ?? {};
                    // reducedData shows label and flattens _Properties to top level for editing
                    const reducedData = { label: nodeData.label, ...propsObj } as any;

                    const handleReducedChange = (reduced: any) => {
                        const { label, ...props } = reduced || {};
                        const updatedNode: any = { ...nodeData };
                        if (label !== undefined) updatedNode.label = label;
                        if (!updatedNode.custom_data) updatedNode.custom_data = {};
                        if (!updatedNode.custom_data.object) updatedNode.custom_data.object = {};
                        updatedNode.custom_data.object._Properties = { ...props };
                        setNodeData(updatedNode);
                    };

                    const handleReducedCommit = (reduced?: any) => {
                        const { label, ...props } = reduced || {};
                        const updatedNode: any = { ...nodeData };
                        if (label !== undefined) updatedNode.label = label;
                        if (!updatedNode.custom_data) updatedNode.custom_data = {};
                        if (!updatedNode.custom_data.object) updatedNode.custom_data.object = {};
                        updatedNode.custom_data.object._Properties = { ...props };
                        // update local state and persist
                        setNodeData(updatedNode);
                        handleSaveMeta(updatedNode as CustomNodeData);
                    };

                    return (
                        <RecursivePropertyGrid
                            data={reducedData}
                            onChange={handleReducedChange}
                            onCommit={handleReducedCommit}
                            displayMode={displayMode}
                            exposeField={exposeField}
                            unexposeField={unexposeField}
                            setPropertyDescription={setPropertyDescription}
                        />
                    );
                })()
            }
            </div>

            <div style={{ padding: 16, flex: '0 0 auto' }}>
                <div style={separatorStyle_h} />
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                    {/* <label style={{ marginRight: 8, fontWeight: 600 , color: '#8d3939ff'}}>Description:</label> */}
                    <p style={{ color: '#555', fontSize: 14 }}> <span style={{ marginRight: 8, fontWeight: 600 , color: '#8d3939ff'}}> Description: </span> {propertyDescription}</p>
                </div>
            </div>

        </div>
    );
};

export default PropertyGridTab;